import { API_URL } from "../config/connect";

// Ескі бекенд чатта `status` / `updated_at` қайтармайды, ал хабарламасы жоқ чатқа
// бос `last_message` объектісін береді — UI күтетін пішінге осында келтіреміз
// (жаңа бекенд бұл өрістерді өзі береді, екеуімен де жұмыс істейді).
const normalizeChat = (chat) => {
    const last = chat.last_message?.id ? chat.last_message : null;
    return {
        ...chat,
        status: chat.status || (chat.deleted_at ? "deleted" : "active"),
        updated_at: chat.updated_at || last?.created_at || chat.created_at,
        last_message: last && { ...last, content: last.message },
    };
};

const normalizeMessage = (msg) => ({
    ...msg,
    sender_name: msg.sender?.name,
    file_info: msg.file_url ? { name: msg.file_name || msg.file_url, url: msg.file_url } : null,
});

const getAdminChats = async (params = {}) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const queryParams = new URLSearchParams();
        
        // Add query parameters
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.search) queryParams.append('search', params.search);
        if (params.status) queryParams.append('status', params.status);
        // «Все статусы» — жасырылған (удалённые) чаттарды да қосамыз.
        else queryParams.append('include_deleted', 'true');
        if (params.date_from) queryParams.append('date_from', params.date_from);
        if (params.date_to) queryParams.append('date_to', params.date_to);
        if (params.sort_by) queryParams.append('sort_by', params.sort_by);
        if (params.sort_order) queryParams.append('sort_order', params.sort_order);

        const endpoint = `${API_URL}/api/v1/admin/chats?${queryParams.toString()}`;

        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch chats. Status: ${response.status}`);
        }

        const data = await response.json();
        return { ...data, items: (data.items || []).map(normalizeChat) };
    } catch (error) {
        console.error("Error fetching admin chats:", error);
        throw error;
    }
};

const getAdminChatDetails = async (chatId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        // Бекендте `GET /admin/chats/{id}` жоқ (тек DELETE → 405). Чат ақпараты
        // мен хабарламалар `/messages` эндпоинтінен бірге келеді.
        const endpoint = `${API_URL}/api/v1/admin/chats/${chatId}/messages?limit=200`;

        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch chat details. Status: ${response.status}`);
        }

        const data = await response.json();
        const messages = (data.messages || []).map(normalizeMessage).reverse(); // бекенд жаңадан ескіге береді
        return {
            chat: data.chat_info && {
                ...normalizeChat(data.chat_info),
                updated_at: messages[messages.length - 1]?.created_at || data.chat_info.created_at,
            },
            messages,
            total: data.total,
        };
    } catch (error) {
        console.error("Error fetching chat details:", error);
        throw error;
    }
};

const deleteAdminChat = async (chatId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const endpoint = `${API_URL}/api/v1/admin/chats/${chatId}`;

        const response = await fetch(endpoint, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to delete chat. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error deleting chat:", error);
        throw error;
    }
};

const deleteAdminMessage = async (chatId, messageId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const endpoint = `${API_URL}/api/v1/admin/messages/${messageId}`;

        const response = await fetch(endpoint, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to delete message. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error deleting message:", error);
        throw error;
    }
};

const archiveAdminChat = async (chatId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const endpoint = `${API_URL}/api/v1/admin/chats/${chatId}/archive`;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to archive chat. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error archiving chat:", error);
        throw error;
    }
};

const unarchiveAdminChat = async (chatId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const endpoint = `${API_URL}/api/v1/admin/chats/${chatId}/unarchive`;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to unarchive chat. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error unarchiving chat:", error);
        throw error;
    }
};

const blockUserFromChat = async (chatId, userId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const endpoint = `${API_URL}/api/v1/admin/chats/${chatId}/block-user`;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ user_id: userId }),
        });

        if (!response.ok) {
            throw new Error(`Failed to block user. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error blocking user:", error);
        throw error;
    }
};

const unblockUserFromChat = async (chatId, userId) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const endpoint = `${API_URL}/api/v1/admin/chats/${chatId}/unblock-user`;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ user_id: userId }),
        });

        if (!response.ok) {
            throw new Error(`Failed to unblock user. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error unblocking user:", error);
        throw error;
    }
};

const getAdminChatStatistics = async (params = {}) => {
    const googleIdToken = localStorage.getItem("google_id_token");

    if (!googleIdToken) {
        throw new Error("No token found. User is not signed in.");
    }

    try {
        const queryParams = new URLSearchParams();
        
        if (params.period) queryParams.append('period', params.period);
        if (params.date_from) queryParams.append('date_from', params.date_from);
        if (params.date_to) queryParams.append('date_to', params.date_to);

        const endpoint = `${API_URL}/api/v1/admin/chats/statistics?${queryParams.toString()}`;

        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${googleIdToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch chat statistics. Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error fetching chat statistics:", error);
        throw error;
    }
};

export {
    getAdminChats,
    getAdminChatDetails,
    deleteAdminChat,
    deleteAdminMessage,
    archiveAdminChat,
    unarchiveAdminChat,
    blockUserFromChat,
    unblockUserFromChat,
    getAdminChatStatistics
};
